// Tender documents as served by GET /tender-documents/{tender_id}
// (simap_core.tender_document_details_v via DynamoDB). Besides the processed
// parts, the list carries the documents the pipeline could NOT read: a
// password-protected file inside the tender ZIP (ProcessingStatus
// 'protected') or an unreadable file ('corrupt'). They have no content, so
// they are never selectable for the chat; the app greys them out and says how
// many documents the assessment is missing. This module owns that logic; the
// JSX only renders.

export type SkippedStatus = 'protected' | 'corrupt';

export interface SkippableDocument {
    DocumentPath: string;
    ProcessingStatus?: string;   // 'complete' (default when absent) | 'protected' | 'corrupt'
    SkipReason?: string;
}

export const isSkipped = (doc: SkippableDocument): boolean =>
    doc.ProcessingStatus === 'protected' || doc.ProcessingStatus === 'corrupt';

// Short label for the row chip and the note.
export const skippedLabel = (status: string | undefined): string =>
    status === 'protected' ? 'Password-protected' : 'Unreadable';

// Readable documents keep every existing behaviour (selection, "n of m
// selected", open); skipped ones are shown apart.
export function splitDocuments<T extends SkippableDocument>(docs: T[]): { readable: T[]; skipped: T[] } {
    const readable: T[] = [];
    const skipped: T[] = [];
    for (const doc of docs) (isSkipped(doc) ? skipped : readable).push(doc);
    return { readable, skipped };
}

// "2 of 9 documents could not be read (1 password-protected, 1 unreadable)".
// readableCount counts document parts (a workbook is several rows), so the
// total is "documents" in the loose sense the list already uses.
export function skippedNote(readableCount: number, skipped: SkippableDocument[]): string {
    if (skipped.length === 0) return '';
    const total = readableCount + skipped.length;
    const protectedCount = skipped.filter(d => d.ProcessingStatus === 'protected').length;
    const unreadableCount = skipped.length - protectedCount;
    const parts: string[] = [];
    if (protectedCount) parts.push(`${protectedCount} password-protected`);
    if (unreadableCount) parts.push(`${unreadableCount} unreadable`);
    return `${skipped.length} of ${total} documents could not be read (${parts.join(', ')})`;
}
